import { createFilterOptions, FilterOptionsState } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

interface IFreeSoloEntryArgs<T extends string | number> {
  /** Off for option-only selects: typed text then only filters, exactly as before. */
  enabled: boolean;
  value: T[];
  options: T[];
  onChange: (values: T[]) => void;
  /** Typed text that spells an option's label offers no `Add`; the option itself is the match. */
  getOptionLabel?: (option: T) => string;
}

/** MUI's dropdown filter ignores case, so matching here does too: `growth` is the `Growth` option. */
function normalize(text: string | number) {
  return String(text).trim().toLowerCase();
}

/**
 * Free-text entry for a multiple `freeSolo` Autocomplete, where MUI alone commits typed text
 * only on Enter and drops it on blur:
 *
 * - Focus out / click out commits whatever is typed as one tag. Values may hold spaces or
 *   commas, so the text is never split; Enter between entries is the only separator.
 * - While typing, an `Add "xxx"` option leads the dropdown, so a mouse user can commit and it
 *   is visible that each entry is confirmed on its own.
 *
 * Not MUI's `autoSelect`: on blur that picks the highlighted option over the typed text, and
 * the highlight follows the first selected value, so tabbing away could toggle a chip off.
 */
export function useFreeSoloEntry<T extends string | number>({
  enabled,
  value,
  options,
  onChange,
  getOptionLabel,
}: IFreeSoloEntryArgs<T>) {
  const [inputValue, setInputValue] = useState('');

  const onInputChange = useCallback((newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  const isSelected = useCallback(
    (text: string) => {
      const wanted = normalize(text);
      return value.some((v) => normalize(v) === wanted);
    },
    [value]
  );
  const findOption = useCallback(
    (text: string) => {
      const wanted = normalize(text);
      return options.find(
        (o) =>
          normalize(o) === wanted || (getOptionLabel !== undefined && normalize(getOptionLabel(o)) === wanted)
      );
    },
    [getOptionLabel, options]
  );
  const isOption = useCallback((text: string) => findOption(text) !== undefined, [findOption]);

  // The filter MUI applies to the dropdown, so "still has matches" below is exactly what the user sees.
  const filter = useMemo(() => createFilterOptions<T>(), []);
  const hasMatchingOptions = useCallback(
    (text: string) =>
      filter(options, { inputValue: text, getOptionLabel: getOptionLabel ?? String }).length > 0,
    [filter, getOptionLabel, options]
  );

  const commitPending = useCallback(() => {
    const text = inputValue.trim();
    if (!enabled || !text) {
      return;
    }
    const option = findOption(text);
    // Typing `par` to find `Partnership` and then clicking Save must not create a `par` label: while
    // the dropdown still offers matches the text is a search, not an entry, so it stays in the box
    // untouched. Fields without options (fund Strategy on creation) match nothing and commit as before.
    if (option === undefined && hasMatchingOptions(text)) {
      return;
    }
    setInputValue('');
    // Text that spells an option's label (or value), in any case, commits that option's real value,
    // not the typed string; only text matching nothing is a new free entry.
    const entry = option ?? (text as T);
    if (!isSelected(String(entry))) {
      onChange([...value, entry]);
    }
  }, [enabled, findOption, hasMatchingOptions, inputValue, isSelected, onChange, value]);

  const filterOptions = useMemo(() => {
    return (opts: T[], state: FilterOptionsState<T>) => {
      const filtered = filter(opts, state);
      const text = state.inputValue.trim();
      if (enabled && text && !isOption(text) && !isSelected(text)) {
        filtered.unshift(text as T);
      }
      return filtered;
    };
  }, [enabled, filter, isOption, isSelected]);

  const isAddOption = useCallback(
    (option: T, state: { inputValue: string }) =>
      enabled && String(option) === state.inputValue.trim() && !isOption(String(option)),
    [enabled, isOption]
  );

  return { commitPending, filterOptions, inputValue, isAddOption, onInputChange };
}

export function addOptionLabel(text: string | number) {
  return `Add "${text}"`;
}
